<script setup lang="ts">
  import { useUserValidation } from '@/composables/validation/use-user-validation'
  import { useUserStore } from '@/store/user.store'
  import { type CreateUser, NestUserRole } from '@/types/user'
  import {
    DropDown,
    FormFieldContainer,
    FormInput,
    Modal,
    UiButton,
  } from '@logistiek-zonder-papier/lzp-library'
  import { isAxiosError } from 'axios'
  import { computed, ref, watch } from 'vue'

  const userStore = useUserStore()

  const show = defineModel<boolean>('show', { required: true })

  const user = ref<CreateUser>(createEmptyUser())
  const submitting = ref<boolean>(false)

  const availableRoles = computed(() => Object.values(NestUserRole).map(role => ({ role })))
  const validation = useUserValidation(user)
  const errorMessage = ref<string | null>(null)

  const emit = defineEmits<{
    created: []
  }>()

  function createEmptyUser() {
    return { firstName: '', lastName: '', email: '', role: NestUserRole.USER }
  }

  watch(
    () => show.value,
    () => {
      validation.submitted.value = false
      user.value = createEmptyUser()
    }
  )

  async function submit() {
    if (!validation.validate()) return
    errorMessage.value = null
    submitting.value = true
    try {
      await userStore.create(user.value)
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        submitting.value = false
        errorMessage.value = error.response.data.message
        return
      }
      throw error
    }
    submitting.value = false
    show.value = false
    emit('created')
  }
</script>

<template>
  <Modal v-model:show="show">
    <template #header>
      <h2>Nieuwe gebruiker</h2>
    </template>
    <FormInput
      type="text"
      v-model="user.firstName"
      label="Voornaam"
      :error="validation.visibleErrors.value.firstName"
    />

    <FormInput
      type="text"
      v-model="user.lastName"
      label="Acthernaam"
      :error="validation.visibleErrors.value.lastName"
    />

    <FormInput
      type="text"
      v-model="user.email"
      label="E-mail"
      :error="validation.visibleErrors.value.email"
    />

    <FormFieldContainer label="Rol" :error="validation.visibleErrors.value.role">
      <DropDown
        :modelValue="{ role: user.role }"
        @update:modelValue="(value: { role: NestUserRole }) => (user!.role = value.role)"
        :data="availableRoles"
        idKey="role"
      >
        <template #item="{ item }">
          <span>{{ item.role }}</span>
        </template>
      </DropDown>
    </FormFieldContainer>

    <div class="error-message" v-if="errorMessage">{{ errorMessage }}</div>

    <template #footer="">
      <UiButton variant="default" label="Sluiten" @click="show = false" />
      <UiButton variant="prominent" label="Opslaan" :disabled="submitting" @click="submit" />
    </template>
  </Modal>
</template>

<style lang="css" scoped>
  .error-message {
    color: var(--colours-support-negative);
  }
</style>
